import { defineComponent as _defineComponent } from 'vue'
import {
  useI18n,
} from "@/composables/useI18n";

import {
  FloatingActionButton,
} from '@/components/buttons';

import {
  computed,
  onMounted,
  ref,
  unref,
  watch,
} from 'vue'

import {
  Issuer,
} from "@/model/Issuer";

import {
  httpMethod
} from "@/scripts/Common/api";

import {
  debounce,
  isEmpty,
} from "@/scripts/Common/HelperFunctions";

import InfoSnackbar from "@/components/children/InfoSnackbar.vue";

import CardImportResultDialog from "@/components/dialogs/CardImportResultDialog.vue";

import type {
  CardImportResult,
  ProfipayCardStates,
} from "@/components/cards/scripts";

import {
  loadCardStates,
} from "@/components/cards/scripts";

import {
  WebChargeStatus,
} from "@/Enums";

import {
  useSnackbar,
} from "@/composables/useSnackbar";

import {
  BulkCreateIssuerWebchargesDialog,
  UpdateIssuerCards,
  WebchargeStateChip,
} from "@/components/issuers/children";

import {
  LastWebchargeState,
  UpdateOrCreateConfiguredWebcharge,
} from "@/components/issuers/scripts/configuredWebcharges";

import type {
  IssuerCard,
} from "@/components/issuers/scripts/issuerCards";

import {
  deleteIssuerCard,
  fetchIssuerCards,
  importIssuerCards,
  issuerCardActions,
  loadLastWebchargeStateForCards,
  updateOrCreateIssuerCardList,
} from "@/components/issuers/scripts/issuerCards";

import {
  fetchIssuers,
} from "@/components/issuers/scripts/issuers/requests";

import {
  isWholeNumber,
} from "@/scripts/Common/validators/defaultValidator";

import {
  useDataTableDefaults,
} from "@/composables/useDataTableDefaults";

import {
  OperationResults,
} from "@/scripts/Common";

import {
  AddCardsDialog,
} from "@/components/cards/children";

import {
  DeleteItemsDialog,
} from "@/components/dialogs";

import {
  hasAccessRights,
} from "@/authorization/AuthorizationService";

import {
  editIssuersPolicy,
} from "@/authorization/policies";

import {
  saveItemToStorage,
} from "@/scripts/Common/componentStorage";

import {
  ISSUER_ID_STORAGE_KEY,
} from "@/components/issuers/scripts";

import {
  DEFAULT_SORT_COLUMN_FETCH_ISSUERS
} from "@/components/issuers/scripts/constants";

import {
  loadIssuerFromStorageOrFirst
} from "./scripts/issuers/helper";

import {
  FetchIssuersParam
} from "@/components/issuers/scripts/issuers/types";

import {
  createOrUpdateConfiguredWebchargeList
} from "@/components/issuers/scripts/configuredWebcharges/requests";

import {
 SelectFileDialog
} from "@/components/dialogs/";

import {
  isCardActive
} from "@/components/children/cardStatusIcon/scripts/helper";

import
  CardStatusIcon
from "@/components/children/cardStatusIcon/CardStatusIcon.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'AdministerIssuerCards',
  emits: [
  'onFetchError',
  'onItemError'
],
  setup(__props, { emit: emits }) {

const {
  t,
} = useI18n();

const {
  dataTableOptions,
  resetPagination,
  dataTableFooter,
} = useDataTableDefaults();



const comboSelection = ref<Issuer | undefined>();
const issuerSearchInput = ref<string | undefined>(undefined);
const selectedIssuerCards = ref<IssuerCard[]>([]);

let ignoreIssuerSearchInputChange = false; // when the issuer is selected from the combobox, the issuerSearchInput change that follows should not trigger a reload of the issuer list

const issuers = ref<Issuer[]>([]);
const selectedIssuer = ref<Issuer>(new Issuer());
const availableIssuerCards = ref<Array<IssuerCard>>([]);
const profipayCardStates = ref<ProfipayCardStates[]>([]);
const lastWebchargeStates = ref<LastWebchargeState[]>([]);

const snackbarRef = ref(null);
const {
  snackbarStatus,
  snackbarText,
  showSnackbarResponse
} = useSnackbar(snackbarRef);

const selectFileDialogRef = ref<InstanceType<typeof SelectFileDialog>>();

const cardImportResultDialogRef = ref(null);
const cardImportResult = ref<CardImportResult>({
  successfulImports: 0,
  failedImports: 0,
  incorrectLinesAsCsv: ""
});

const isAddDialogVisible = ref<boolean>(false);
const isDeleteDialogVisible = ref<boolean>(false);
const isUpdateDialogVisible = ref<boolean>(false);
const isBulkAddWebchargeVisible = ref<boolean>(false);

const cardNumberFilter = ref<string | undefined>(undefined);

const headers = computed(() => {
  return [
    {
      text: t('card_number'),
      value: 'cardNumber',
    },
    {
      text: t('webcharge_count'),
      value: 'webchargeCount',
      sortable: false
    },
    {
      text: t('last_webcharge_state'),
      value: 'lastWebchargeState',
      sortable: false
    },
    {
      text: t('actions'),
      value: 'edit',
      sortable: false
    }
  ]
});

const selectedCardNumbers = computed(
  () => unref(selectedIssuerCards)?.map((card => card.cardNumber))
);

const selectedActiveCardIds = computed(
  () => unref(selectedIssuerCards.value.filter(card => isCardActive(card.cardNumber, unref(profipayCardStates))).map(card => card.cardId))
);

const hasNoEditRights = computed(() => {
  return !hasAccessRights(editIssuersPolicy);
});

const canAddIssuerCard = computed(() => {
  return !selectedIssuer.value.IsNew
});

const onHandleFetchError = ({error}: { error: any }) => {
  emits('onFetchError', {error})
};

const onHandleItemError = ({response}: { response: OperationResults<any> }) => {
  emits('onItemError', {response})
};

const initializeIssuer = async () => {
  const {response, error} = await loadIssuerFromStorageOrFirst();

  if (error) {
    onHandleFetchError({error});
    return;
  }

  selectedIssuer.value = response ?? new Issuer();
  comboSelection.value = response;
}

const loadIssuers = async () => {

  const fetchArgs: FetchIssuersParam = {
    sortColumn: DEFAULT_SORT_COLUMN_FETCH_ISSUERS
  };

  if (issuerSearchInput.value) fetchArgs.companyName = issuerSearchInput.value;

  const {response, error} = await fetchIssuers(fetchArgs);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  issuers.value = response ?? [];
}

const getLastWebchargeState = (cardNumber: number) => {
  const state = lastWebchargeStates.value.find(element => element.cardNumber === cardNumber);

  return state
    ? state
    : {
      cardNumber: cardNumber,
      lastChargeDate: null,
      lastChargeStatus: WebChargeStatus.Unknown,
      lastChargeProtocolId: 0
    };
}

const loadProfipayCardStates = async (cardNumbers: number[]) => {

  // we will send a request with the card numbers as a filter
  // therefore we want to return the first page with the same amount of items as the card numbers that we send
  const {
    error,
    response,
  } = await loadCardStates(cardNumbers, { page: 1, itemsPerPage: cardNumbers.length });

  if (error) {
    onHandleFetchError({error});
    return;
  }

  profipayCardStates.value = response;
}

const loadLastWebchargeStates = async (cardNumbers: number[]) => {
  const {
    error,
    response,
  } = await loadLastWebchargeStateForCards(cardNumbers, { page: 1, itemsPerPage: cardNumbers.length });

  if (error) {
    onHandleFetchError({error});
    return;
  }

  lastWebchargeStates.value = response;
}

const loadIssuerCards = async () => {
  if (selectedIssuer.value.IsNew || selectedIssuer.value.Id === undefined) return;

  const {response, error} = await fetchIssuerCards(selectedIssuer.value.Id, dataTableOptions, cardNumberFilter.value);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  // fetch further data for the cards
  if (response && response.length > 0) {
    const cardNumbers = (response as IssuerCard[]).map(element => element.cardNumber);
    const selectedCardNumbers = selectedIssuerCards.value.map(element => element.cardNumber);

    //profipay states need to be gathered for selected and available cards to create webcharges for all selected cards
    const selectedAndAvailableCards = cardNumbers.concat(selectedCardNumbers);

    await Promise.all([
      loadProfipayCardStates(selectedAndAvailableCards),
      loadLastWebchargeStates(cardNumbers)
    ]);
  }

  availableIssuerCards.value = response;
};

const refresh = async () => {
  await loadIssuers();
  await loadIssuerCards();
};

const onHandleImportFile = () => {
  selectFileDialogRef.value?.show();
};

const onHandleAddIssuerCards = () => {
  isAddDialogVisible.value = true;
};

const onHandleAddIssuerWebcharge = () => {
  isBulkAddWebchargeVisible.value = true;
};

const cardActionMap = (operationType: string) => {
  const items = {
    [issuerCardActions.createIssuerCards]: t('action_create'),
    [issuerCardActions.updateIssuerCards]: t('action_update'),
    [issuerCardActions.deleteIssuerCards]: t('action_delete'),
  }

  return items[operationType as issuerCardActions];
};

const setSnackbarStatus = ({response}: { response: Record<any, any> }) => showSnackbarResponse(response, cardActionMap);

const onHandleImportConfirm = async (file: any) => {

  const {response, error} = await importIssuerCards(file);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  cardImportResult.value = response;

  if (cardImportResult.value.incorrectLinesAsCsv !== null) {
    (cardImportResultDialogRef.value as any).show();
  }

  setSnackbarStatus({response});
};

const updateOrCreateIssuerCards = async (method: httpMethod, listOfCardNumbers: number[], issuerId?: number) => {
  const id = (issuerId ?? selectedIssuer.value.Id);
  if(id === undefined) return;

  const { response, error } = await updateOrCreateIssuerCardList(method, listOfCardNumbers, id);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  setSnackbarStatus({response});
  loadIssuerCards();
};

const createConfiguredWebcharges = async (webcharges: UpdateOrCreateConfiguredWebcharge[]) => {
  const { error, response } = await createOrUpdateConfiguredWebchargeList(httpMethod.POST, webcharges);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  setSnackbarStatus({response});
  loadIssuerCards();
}

const onHandleDialogAddConfirm = async (values: { cardStart: number, cardEnd: number }) => {
  isAddDialogVisible.value = false;

  const {
    cardEnd,
    cardStart,
  } = values;

  const counter = cardEnd - cardStart + 1;

  const cardNumbersToCreate = [...Array(counter)].map((count: number, index: number) => {
    return cardStart + index
  });

  await updateOrCreateIssuerCards(httpMethod.POST, cardNumbersToCreate);
}

const onHandleDialogAddCancel = () => {
  isAddDialogVisible.value = false;
};

const removeCards = async (listOfCardNumbers: number[]) => {
  if(selectedIssuer.value.Id === undefined) return;

  const { response, error } = await deleteIssuerCard(listOfCardNumbers, selectedIssuer.value.Id);

  if (error) {
    onHandleFetchError({error});
    return;
  }

  if (response.failedItems.length > 0) {
    onHandleItemError({response});
  }

  setSnackbarStatus({response});
  loadIssuerCards();
};
const onHandleDialogDeleteConfirm = async () => {
  isDeleteDialogVisible.value = false;
  const cardIdsToDelete = selectedIssuerCards.value.map((selectedCard) => selectedCard.cardNumber);

  await removeCards(cardIdsToDelete);
  resetSelectedIssuerCards();
};

const onHandleDialogDeleteCancel = () => {
  isDeleteDialogVisible.value = false;
};

const onHandleDialogUpdateConfirm = async (values: { issuerId: number }) => {
  const {issuerId} = values;
  const cardNumbersToUpdate = selectedIssuerCards.value.map((selectedCard) => selectedCard.cardNumber);

  await updateOrCreateIssuerCards(httpMethod.PUT, cardNumbersToUpdate, issuerId);
  isUpdateDialogVisible.value = false;
  resetSelectedIssuerCards();
};

const onHandleDialogCreateWebchargesCancel = () => {
  isBulkAddWebchargeVisible.value = false;
};

const onHandleDialogCreateWebchargesConfirm = async (webchargesToCreate: UpdateOrCreateConfiguredWebcharge[]) => {
  await createConfiguredWebcharges(unref(webchargesToCreate));
  isBulkAddWebchargeVisible.value = false;
};

const onHandleDialogUpdateCancel = () => {
  isUpdateDialogVisible.value = false;
};

const onHandleItemUpdateClick = (item: IssuerCard) => {
  selectedIssuerCards.value = [item];
  isUpdateDialogVisible.value = true;
}

const onHandleItemDeleteClick = (item: IssuerCard) => {
  selectedIssuerCards.value = [item];
  isDeleteDialogVisible.value = true;
}

const onHandleUpdateBatch = () => {
  isUpdateDialogVisible.value = true;
};
const onHandleDeleteBatch = () => {
  isDeleteDialogVisible.value = true;
};

const resetSelectedIssuerCards = () => {
  selectedIssuerCards.value = [];
}

watch(() => comboSelection.value, async () => {
  if (comboSelection.value && comboSelection.value instanceof Issuer) {
    selectedIssuer.value = comboSelection.value;
    await loadIssuerCards();
  }
});

watch(() => [
  dataTableOptions.value.pagination.page,
  dataTableOptions.value.pagination.itemsPerPage,
  dataTableOptions.value.sorting,
], async () => {
  resetSelectedIssuerCards();
  await loadIssuerCards();
}, {deep: true});

watch(() => cardNumberFilter.value, debounce(async () => {
  if (isEmpty(cardNumberFilter.value)) cardNumberFilter.value = undefined;
  if (!cardNumberFilter.value || isWholeNumber(cardNumberFilter.value)) {
    dataTableOptions.value.pagination.page = 1;
    resetSelectedIssuerCards();
    await loadIssuerCards();
  }
}));

const onHandleSelectionChange = () => {
  const issuerId = comboSelection.value?.Id;
  if (!issuerId || !(comboSelection.value instanceof Issuer)) return;
  saveItemToStorage(issuerId, ISSUER_ID_STORAGE_KEY);

  resetSelectedIssuerCards();
  resetPagination();
  cardNumberFilter.value = undefined;
}

watch(() => issuerSearchInput.value, debounce(async () => {
  if (ignoreIssuerSearchInputChange) {
    ignoreIssuerSearchInputChange = false;
    return;
  }
  await loadIssuers();
}));

onMounted(async () => {
  await initializeIssuer();
  loadIssuerCards();
});


return { __sfc: true,t, dataTableOptions, resetPagination, dataTableFooter, emits, comboSelection, issuerSearchInput, selectedIssuerCards, ignoreIssuerSearchInputChange, issuers, selectedIssuer, availableIssuerCards, profipayCardStates, lastWebchargeStates, snackbarRef, snackbarStatus, snackbarText, showSnackbarResponse, selectFileDialogRef, cardImportResultDialogRef, cardImportResult, isAddDialogVisible, isDeleteDialogVisible, isUpdateDialogVisible, isBulkAddWebchargeVisible, cardNumberFilter, headers, selectedCardNumbers, selectedActiveCardIds, hasNoEditRights, canAddIssuerCard, onHandleFetchError, onHandleItemError, initializeIssuer, loadIssuers, getLastWebchargeState, loadProfipayCardStates, loadLastWebchargeStates, loadIssuerCards, refresh, onHandleImportFile, onHandleAddIssuerCards, onHandleAddIssuerWebcharge, cardActionMap, setSnackbarStatus, onHandleImportConfirm, updateOrCreateIssuerCards, createConfiguredWebcharges, onHandleDialogAddConfirm, onHandleDialogAddCancel, removeCards, onHandleDialogDeleteConfirm, onHandleDialogDeleteCancel, onHandleDialogUpdateConfirm, onHandleDialogCreateWebchargesCancel, onHandleDialogCreateWebchargesConfirm, onHandleDialogUpdateCancel, onHandleItemUpdateClick, onHandleItemDeleteClick, onHandleUpdateBatch, onHandleDeleteBatch, resetSelectedIssuerCards, onHandleSelectionChange, FloatingActionButton, InfoSnackbar, CardImportResultDialog, BulkCreateIssuerWebchargesDialog, UpdateIssuerCards, WebchargeStateChip, AddCardsDialog, DeleteItemsDialog, SelectFileDialog, isCardActive, CardStatusIcon }
}

})